import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IRecordTypeOverride } from '@platform/core/types';
import {
  useGlobalRecordTypeOverrideQuery, useMeQuery, useRecordTypeOverrideQuery, useResetGlobalRecordTypeOverrideMutation, useResetRecordTypeOverrideMutation,
  useSaveGlobalRecordTypeOverrideMutation, useSaveRecordTypeOverrideMutation,
} from '@platform/state/api';
import { useAppDispatch } from '@platform/state/hooks';
import { toastShown } from '@platform/state/slices/uiSlice';
import type { IRecordTypeEditor } from './IRecordTypeEditor';
import { saveErrorText } from './saveErrorText';

/** Глобальні ролі, що змінюють глобальні визначення (прототип v4 canCustGlobal). */
const GLOBAL_ROLES = ['Platform admin', 'Customizer'];

/**
 * Перевизначення типу запису для конструктора й редактора форми: простору (ADR-023) чи глобальне (`global`, ADR-027 — для всіх робочих
 * просторів без власного налаштування). Кожна зміна одразу йде PUT …/override з If-Match поточної версії; поки збереження триває, наступна
 * зміна чекає в черзі (остання перемагає) і йде з новою версією. Застаріла версія чи помилка — патч перечитується, повідомлення пояснює
 * причину. Змінює той, хто має можливість customize у просторі (глобально — адміністратор платформи чи налаштувальник).
 */
export function useRecordTypeEditor(ws: string, moduleKey: string, rtKey: string, global = false): IRecordTypeEditor {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { data: me } = useMeQuery();
  const wsDoc = useRecordTypeOverrideQuery({ ws, rt: rtKey }, { skip: global || !ws || !rtKey });
  const globalDoc = useGlobalRecordTypeOverrideQuery({ rt: rtKey }, { skip: !global || !rtKey });
  const { data: doc, refetch } = global ? globalDoc : wsDoc;
  const [saveWs] = useSaveRecordTypeOverrideMutation();
  const [saveGlobal] = useSaveGlobalRecordTypeOverrideMutation();
  const [resetWs] = useResetRecordTypeOverrideMutation();
  const [resetGlobal] = useResetGlobalRecordTypeOverrideMutation();
  const [pending, setPending] = useState<IRecordTypeOverride | null>(null);
  const busy = useRef(false);
  const queued = useRef<IRecordTypeOverride | null>(null);
  const version = useRef(0);
  useEffect(() => { if (doc && !busy.current) version.current = doc.version; }, [doc]);
  useEffect(() => { setPending(null); queued.current = null; }, [ws, rtKey, global]);
  const fail = useCallback((e: unknown) => {
    dispatch(toastShown({ kind: 'error', text: saveErrorText(e, t('rtBuilder.conflict'), t('rtBuilder.saveFailed')) }));
  }, [dispatch, t]);
  const send = useCallback(async (patch: IRecordTypeOverride) => {
    busy.current = true;
    try {
      const arg = { rt: rtKey, module: moduleKey, patch, version: version.current };
      await (global ? saveGlobal(arg) : saveWs({ ws, ...arg })).unwrap();
      version.current += 1;
    } catch (e) {
      fail(e);
      queued.current = null;
      setPending(null);
      void refetch();
    } finally {
      busy.current = false;
    }
    const next = queued.current;
    queued.current = null;
    if (next) await send(next);
  }, [global, saveGlobal, saveWs, ws, rtKey, moduleKey, fail, refetch]);
  const current = useMemo<IRecordTypeOverride>(() => pending ?? doc?.patch ?? {}, [pending, doc]);
  const apply = useCallback((edit: (patch: IRecordTypeOverride) => IRecordTypeOverride) => {
    const next = edit(structuredClone(current));
    setPending(next);
    if (busy.current) queued.current = next;
    else void send(next);
  }, [current, send]);
  const reset = useCallback(() => {
    setPending(null);
    const arg = { rt: rtKey, module: moduleKey };
    (global ? resetGlobal(arg) : resetWs({ ws, ...arg })).unwrap().catch(fail);
  }, [global, resetGlobal, resetWs, ws, rtKey, moduleKey, fail]);
  const canEdit = global ? GLOBAL_ROLES.includes(me?.globalRole ?? '') : !!me?.workspaces.find((w) => w.id === ws)?.myCapabilities.includes('customize');
  return { patch: current, loaded: !!doc, customized: !!doc && doc.version > 0 && Object.keys(current).length > 0, apply, reset, canEdit };
}
